import moment from "moment";
import {
  additionalInformationLabels,
  behaviorLabels,
  behaviourNoteMapping,
  clientCareNeedLabel,
  mobilityDeviceEnumMap,
  relationshipEnumMap,
  serviceLabels,
  vehicleEnumMap,
} from "../enum/formEnum";
const capitalizeFirst = (str) =>
  typeof str === "string" && str.length > 0
    ? str.charAt(0).toUpperCase() + str.slice(1).toLowerCase()
    : str;
const shouldShowAdditionalField = (key, info) => {
  const ltcFields = [
    "policyNumber",
    "ltcInsuranceProvider",
    "ltcInsuranceAcknowledgment",
  ];

  if (key === "petDetails") return info?.petsPresent === true;
  if (ltcFields.includes(key)) return info?.longTermCareInsurance === true;
  if (key === "firearmsSecure") return info?.firearmsPresent === true;
  if (key === "learnMore") return info?.learnMore === true;

  return true;
};
export const getPdfFormSections = (formData) => [
  {
    title: "Form Submission Details",
    data: [
      ["City", formData?.formSubmissionDetails?.city || "-"],
      [
        "Community Liaison",
        formData?.formSubmissionDetails?.communityLiaison || "-",
      ],
      [
        "Are you filling out this form for someone else?",
        formData?.formSubmissionDetails?.isFillingForSomeoneElse || "-",
      ],
      [
        "Who is filling out this form?",
        relationshipEnumMap[formData?.formSubmissionDetails?.fillerIdentity] ||
          "Self",
      ],
      [
        "Filler's First Name",
        formData?.formSubmissionDetails?.fillersFirstName || "-",
      ],
      [
        "Filler's Last Name",
        formData?.formSubmissionDetails?.fillersLastName || "-",
      ],
      ["Filler's Phone", formData?.formSubmissionDetails?.fillersPhone || "-"],
      ["Email Address", formData?.formSubmissionDetails?.email || "-"],
      [
        "Please list who referred you to Cambridge Caregivers",
        formData?.formSubmissionDetails?.referralSource || "-",
      ],
    ],
  },
  {
    title: "Client Information",
    data: [
      ["First Name", formData?.clientInformation?.firstName || "-"],
      ["Last Name", formData?.clientInformation?.lastName || "-"],
      [
        "Date of Birth",
        formData?.clientInformation?.dateOfBirth
          ? moment(formData.clientInformation.dateOfBirth).format("MM/DD/YYYY")
          : "-",
      ],
      ["Gender", formData?.clientInformation?.gender || "-"],
      ["Height (Feet)", formData?.clientInformation?.heightFeet ?? "-"],
      ["Height (Inches)", formData?.clientInformation?.heightInches ?? "-"],
      ["Weight (lbs)", formData?.clientInformation?.weight ?? "-"],
    ],
  },
  {
    title: "Service Schedule",
    data: [
      [
        "Start Date",
        moment(formData?.serviceDetails?.startDate).format("MM/DD/YYYY"),
      ],
      [
        "Caregiver Preference",
        formData?.serviceDetails?.caregiverPreference || "-",
      ],
      ["Special Requests", formData?.serviceDetails?.specialRequests || "-"],
    ],
  },
  {
    title: "Schedule: Variable and Flexible",
    data: [
      [
        "Schedule Type",
        formData?.scheduleVariableFlexible?.scheduleType || "-",
      ],
      ...(formData?.scheduleVariableFlexible?.dayTimes?.days?.length > 0
        ? formData?.scheduleVariableFlexible?.dayTimes?.days?.map((day) => {
            const start =
              formData.scheduleVariableFlexible.dayTimes?.dayTimes?.[day]
                ?.startTime;
            const end =
              formData.scheduleVariableFlexible.dayTimes?.dayTimes?.[day]
                ?.endTime;
            const formatTime = (t) =>
              t ? moment(t, "HH:mm").format("hh:mm A") : "-";

            return [
              `${day} Time`,
              `${formatTime(start)} to ${formatTime(end)}`,
            ];
          })
        : [["Schedule Days", "No schedule selected"]]),
    ],
  },
  {
    title: "Individualized Service Plan",
    data: Object.entries(serviceLabels)
      .filter(([key]) => {
        const data = formData?.variableFlexibleServices;

        switch (key) {
          case "vehiclePreference":
            return data?.transportationNeeded === true;
          case "assistanceEatingNotes":
            return data?.eating === true;
          case "specifyOther":
            return data?.eating === true && !!data?.assistanceEatingNotes;
          default:
            return true;
        }
      })
      .map(([key, label]) => {
        const value = formData?.variableFlexibleServices?.[key];

        switch (key) {
          case "vehiclePreference":
            return [label, vehicleEnumMap[value] || value];
          default:
            if (!value) {
              return [label, "Not Required"];
            } else if (value === true) {
              return [label, "Required"];
            } else {
              return [label, value];
            }
        }
      }),
  },
  {
    title: "Client Care Needs",
    data: Object.entries(clientCareNeedLabel)
      .filter(([key]) => {
        const care = formData?.clientCareNeeds;
        if (
          key === "isMealtimeAssistanceLevel" &&
          care?.isMealtimeAssistance !== "Yes"
        )
          return false;
        if (
          key === "isShoweringAssistanceLevel" &&
          care?.isShoweringAssistance !== "Yes"
        )
          return false;
        if (
          key === "isMedicationRemindersResponse" &&
          care?.isMedicationReminders !== "Yes"
        )
          return false;
        if (key === "swallowingOther" && care?.swallowing !== "Other")
          return false;

        return true;
      })
      .map(([key, label]) => {
        const value = formData?.clientCareNeeds?.[key];

        if (Array.isArray(value)) {
          return [
            label,
            value.length > 0 ? value.map(capitalizeFirst).join(", ") : "N/A",
          ];
        } else if (typeof value === "string") {
          return [label, value.trim() !== "" ? capitalizeFirst(value) : "N/A"];
        } else if (typeof value === "boolean") {
          return [label, value ? "Yes" : "No"];
        } else {
          return [label, "-"];
        }
      }),
  },
  {
    title: "Mobility & Assistance",
    data: [
      [
        "Uses Wheelchair",
        formData?.mobilityAssistanceHeading?.usesWheelchair ? "Yes" : "No",
      ],
      [
        "Mobility Assistance",
        mobilityDeviceEnumMap[
          formData?.mobilityAssistanceHeading?.mobilityAssistance
        ] || "N/A",
      ],
    ],
  },
  {
    title: "Behavior & Cognition",
    data: Object.entries(behaviorLabels)
      .filter(([key]) => {
        let value = formData?.behaviorCognition?.[key];
        const isNotesField = key.endsWith("Notes");
        if (isNotesField) {
          const noteMapping = behaviourNoteMapping[key];
          if (
            noteMapping &&
            !value &&
            formData?.behaviorCognition?.[noteMapping] === true
          ) {
            value = "-";
          }
        }
        return !(
          isNotesField &&
          (!value || typeof value !== "string" || value.trim() === "")
        );
      })
      .map(([key, label]) => {
        let value = formData?.behaviorCognition?.[key];
        const isNotes = key.endsWith("Notes");
        if (isNotes) {
          const noteMapping = behaviourNoteMapping[key];
          if (
            noteMapping &&
            !value &&
            formData?.behaviorCognition?.[noteMapping] === true
          ) {
            value = "-";
          }
        }
        const displayValue = isNotes
          ? value.trim()
          : value
            ? "Required"
            : "Not Required";
        return [label, displayValue];
      }),
  },
  {
    title: "Additional Information",
    data: Object.entries(additionalInformationLabels)
      .filter(([key]) => {
        // Show allergiesNote only if allergies array has at least one value
        if (key === "allergiesNote") {
          const allergies = formData?.additionalInformation?.allergies;
          return Array.isArray(allergies) && allergies.length > 0;
        }
        return shouldShowAdditionalField(key, formData?.additionalInformation);
      })
      .flatMap(([key, label]) => {
        const value = formData?.additionalInformation?.[key];

        // Handle petDetails as multiple rows
        if (key === "petDetails" && Array.isArray(value)) {
          return value.map((pet, index) => [
            `Pet Details ${index + 1}`,
            `Type: ${pet.petType ?? "-"}, Weight: ${pet.petWeight ?? "-"} lbs, Size: ${pet.size ?? "-"}`,
          ]);
        }

        // Handle allergies
        if (key === "allergies") {
          if (Array.isArray(value) && value.length > 0) {
            return [[label, value.join(", ")]];
          }
          return [[label, "No"]];
        }

        // Handle allergiesNote empty value
        if (key === "allergiesNote" && !value) {
          return [[label, "-"]];
        }

        // Format standard boolean / string fields
        if (value === true) {
          return [[label, "Yes"]];
        } else if (value === false || value === 0) {
          return [[label, "No"]];
        } else if (typeof value === "string" && value.trim()) {
          return [[label, value]];
        }

        // Fallback for empty or undefined values
        return [[label, "No"]];
      }),
  },
  {
    title: "Contact Information",
    data: [
      [
        "Street Address",
        `${formData?.serviceAddressSection0?.serviceAddressStreet}${formData?.serviceAddressSection0?.serviceAddress1 ? `, ${formData?.serviceAddressSection0?.serviceAddress1}` : ""}` ||
          "-",
      ],
      ["City", formData?.serviceAddressSection0?.serviceAddressCity || "-"],
      ["State", formData?.serviceAddressSection0?.serviceAddressState || "-"],
      [
        "ZIP Code",
        formData?.serviceAddressSection0?.serviceAddressZipCode || "-",
      ],
      ["Home Phone", formData?.serviceAddressSection0?.homePhone || "-"],
      ["Mobile Phone", formData?.serviceAddressSection0?.mobilePhone || "-"],
      [
        "Preferred Phone",
        formData?.serviceAddressSection0?.preferredPhone || "-",
      ],
      ["Email", formData?.serviceAddressSection0?.email || "-"],
    ],
  },
  {
    title: "Care Status",
    data: [
      [
        "Hospice Status",
        formData?.careStatusSection0?.hospiceStatus ? "Yes" : "No",
      ],
      ...(formData?.careStatusSection0?.hospiceStatus
        ? [
            [
              "Hospice Agency",
              formData?.careStatusSection0?.hospiceAgency || "-",
            ],
            [
              "Representative Name",
              formData?.careStatusSection0?.hospiceRepName || "-",
            ],
            [
              "Representative Phone",
              formData?.careStatusSection0?.hospiceRepPhone || "-",
            ],
          ]
        : []),
      [
        "Home Health Status",
        formData?.careStatusSection0?.homeHealthStatus ? "Yes" : "No",
      ],
      ...(formData?.careStatusSection0?.homeHealthStatus
        ? [
            ["Home Agency", formData?.careStatusSection0?.homeAgency || "-"],
            [
              "Home Representative Name",
              formData?.careStatusSection0?.homeRepName || "-",
            ],
            [
              "Home Representative Phone",
              formData?.careStatusSection0?.homeRepPhone || "-",
            ],
          ]
        : []),
    ],
  },
  {
    title: "Primary Care Physician",
    data: [
      ["First Name", formData?.primaryCarePhysician?.physicianFirstName || "-"],
      ["Last Name", formData?.primaryCarePhysician?.physicianLastName || "-"],
      [
        "Phone Number",
        formData?.primaryCarePhysician?.physicianPhoneNumber || "-",
      ],
    ],
  },
  {
    title: "Additional Information",
    data: [
      [
        "Additional Details",
        formData?.additionalInformation1?.additionalInfo || "-",
      ],
    ],
  },
  {
    title: "Signature",
    data: [["Name of Signer", formData?.signatureInfo?.NameOfSigner || "-"]],
  },
];
